import { type Components, type CSSObject, type Interpolation, keyframes, type Theme } from '@mui/material/styles'
import type { ComponentProps } from 'react'
import type { DataGridProps } from 'react-data-grid'
import type { XwiDataGrid } from '../components/XwiDataGrid.tsx'

declare module '@mui/material/styles' {
	interface ComponentNameToClassKey {
		XwiDataGrid: 'root'
	}

	interface Components {
		XwiDataGrid?: {
			defaultProps?: Partial<DataGridProps<unknown>>
			variants: {
				props: Partial<ComponentProps<typeof XwiDataGrid>>
				style: Interpolation<{ theme: Theme }>
			}[]
			styleOverrides?: {
				root?: ({ theme }: { theme: Theme }) => CSSObject
			}
		}
	}
}

const fadeShadow = keyframes`
	0 { opacity: 1; }
	90% { opacity: 1; }
	100% { opacity: 0; }
`

export const components: Components<Theme> = {
	XwiDataGrid: {
		defaultProps: {
			rowHeight: 40,
			headerRowHeight: 40,
			enableVirtualization: true,
			defaultColumnOptions: {
				minWidth: 24,
			},
		},
		variants: [
			{
				props: { fade: true },
				style: ({ theme }) => ({
					'&:after': {
						content: '""',
						gridColumn: '1 / -1',
						gridRow: '1 / -1',
						pointerEvents: 'none',
						background:
							theme.palette.mode === 'dark'
								? 'linear-gradient(90deg,rgba(20, 22, 25, 0) 0%, rgba(20, 22, 25, 0) 92vw, rgba(20, 22, 25, 1) 100vw)'
								: 'linear-gradient(90deg,rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 92vw, rgba(255, 255, 255, 1) 100vw)',
						backgroundAttachment: 'fixed',
						animation: `${fadeShadow} 3s`,
						animationTimeline: '--scroll-grid',
						zIndex: 9999,
					},
				}),
			},
			{
				props: { alignTop: true },
				style: () => ({
					'& .rdg-cell:not([role=columnheader])': {
						paddingTop: 8,
						alignContent: 'flex-start',
					},
				}),
			},
		],
		styleOverrides: {
			root: ({ theme }) => ({
				'&.rdg': {
					color: theme.palette.text.primary,
					'--rdg-header-background-color': theme.palette.background.paper,
					'--rdg-cell-frozen-box-shadow': `inset -1px 0 0 ${theme.palette.grey[300]}`,
					height: '100%',
					border: 'none',
					backgroundColor: 'transparent',
					position: 'relative',
					scrollTimelineName: '--scroll-grid',
					scrollTimelineAxis: 'inline',
					scrollbarColor: `${theme.palette.grey[300]} transparent`,

					'& .rdg-row': {
						backgroundColor: theme.palette.background.paper,
						transition: 'background 0.2s ease-out',
						':hover .rdg-cell-frozen-start, :hover .rdg-cell-frozen-end': { backgroundColor: theme.palette.background.hover },
						':hover': { backgroundColor: theme.palette.background.hover },
						'&[aria-selected=true]': { backgroundColor: theme.palette.background.selected },
						'&.error-row': { backgroundColor: theme.palette.error.light },
						'&.selected': { backgroundColor: theme.palette.background.selected },
					},
					'& .rdg-cell': {
						borderBottom: `1px solid ${theme.palette.dividerSubtle}`,
						borderRight: 0,
						color: theme.palette.text.primary,

						'&.align-right': { textAlign: 'right' },
						'&.align-center': { textAlign: 'center' },
						'&.align-left': { textAlign: 'left' },
						'&.no-padding': { padding: '0 !important' },
						'&[aria-selected=true]': { outline: 'none' },
					},

					'& .rdg-cell-frozen-start, & .rdg-cell-frozen-end': {
						backgroundColor: theme.palette.background.paper,
						transition: 'background 0.2s ease-out',
						overflow: 'visible',
					},

					'& .rdg-header-row .rdg-cell': {
						borderBottom: `1px solid ${theme.palette.divider}`,
						color: theme.palette.text.secondary,
						fontSize: theme.typography.body1.fontSize,
						fontWeight: 350,
						letterSpacing: '0.05em',
						zIndex: 9999,

						'&:not(:last-child):after': {
							content: '""',
							position: 'absolute',
							right: 0,
							bottom: 0,
							height: 16,
							borderRight: `1px solid ${theme.palette.divider}`,
						},
						'&.rdg-cell-frozen-start, &.rdg-cell-frozen-end': {
							borderRight: 'none !important',
							zIndex: 10_000,
						},
						'.MuiIconButton-root .MuiSvgIcon-root': { color: theme.palette.text.secondary },
					},

					'& .rdg-header-sort-name': {
						width: 'calc(100% - 20px)',
						'&+ span': {
							marginLeft: 4,
							'&:empty': {
								display: 'none',
							},
						},
					},

					'& .measurement': {
						position: 'relative',
						height: '100%',
						width: '100%',
						paddingInline: 8,
						display: 'flex',
						justifyContent: 'flex-end',
						alignItems: 'center',
						fontFamily: theme.typography.fontFamily,
						fontSize: '1rem',
						letterSpacing: '0.05em',
					},
					'& .qc-warning': {
						color: theme.palette.text.secondary,
					},
					'& .active-alert': {
						backgroundColor: theme.palette.error.light,
						color: theme.palette.error.dark,
						justifyContent: 'space-between',
					},
				},
			}),
		},
	},

	MuiLink: { defaultProps: { underline: 'hover', color: 'inherit' } },
}
